import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Bot, Send, Plus, MessageSquare, Loader2, Sparkles, Trash2, Menu, X,
} from 'lucide-react';

function renderRich(text) {
  // Simple bold + line-break renderer (no external md dep)
  const lines = String(text || '').split('\n');
  return lines.map((line, li) => {
    const parts = [];
    let last = 0;
    const re = /\*\*([^*]+)\*\*/g;
    let m;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      parts.push(<strong key={`b-${li}-${m.index}`} className="font-black">{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return (
      <span key={`l-${li}`}>
        {parts.length ? parts : line}
        {li < lines.length - 1 && <br />}
      </span>
    );
  });
}

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`} data-testid={`msg-${msg.role}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-500 flex items-center justify-center shrink-0 mr-2 mt-1">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`max-w-[80%] p-3 rounded-2xl ${
        isUser
          ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white rounded-br-sm'
          : 'bg-card border border-white/10 rounded-bl-sm'
      }`}>
        <div className="text-sm leading-relaxed">{renderRich(msg.text)}</div>
      </div>
    </div>
  );
}

export default function AICoachPage() {
  const { getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSess, setLoadingSess] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    setLoadingSess(true);
    try {
      const res = await fetch(`${API}/coach/sessions`, { headers: getAuthHeaders() });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch { /* noop */ }
    setLoadingSess(false);
  }, [getAuthHeaders]);

  const loadSession = useCallback(async (id) => {
    setActiveId(id);
    setMessages([]);
    try {
      const res = await fetch(`${API}/coach/sessions/${id}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setMessages(data.messages || []);
    } catch { /* noop */ }
  }, [getAuthHeaders]);

  const createSession = async () => {
    try {
      const res = await fetch(`${API}/coach/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      setSessions(s => [data, ...s]);
      setActiveId(data.session_id);
      setMessages([]);
      setSidebarOpen(false);
    } catch { toast.error('Could not create session'); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await fetch(`${API}/coach/sessions/${id}/delete`, { method: 'POST', headers: getAuthHeaders() });
      setSessions(s => s.filter(x => x.session_id !== id));
      if (activeId === id) { setActiveId(null); setMessages([]); }
    } catch { toast.error('Delete failed'); }
  };

  const send = async () => {
    if (!input.trim() || !activeId || sending) return;
    const text = input.trim();
    const tmpUser = { message_id: 'tmp-'+Date.now(), role: 'user', text, created_at: new Date().toISOString() };
    setMessages(m => [...m, tmpUser]);
    setInput('');
    setSending(true);
    try {
      const res = await fetch(`${API}/coach/sessions/${activeId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMessages(m => [
        ...m.filter(x => x.message_id !== tmpUser.message_id),
        data.user_message,
        data.reply,
      ]);
      // Refresh session list for auto-title update
      loadSessions();
    } catch {
      toast.error('Message failed');
      setMessages(m => m.filter(x => x.message_id !== tmpUser.message_id));
    }
    setSending(false);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    loadSessions();
  }, [authLoading, isAuthenticated, loadSessions, navigate]);

  useEffect(() => {
    if (!activeId && sessions.length > 0) {
      loadSession(sessions[0].session_id);
    }
  }, [sessions, activeId, loadSession]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const suggestions = [
    "What's the fastest way to grow my audience this week?",
    "How do I hit $50 in earnings by month-end?",
    "Should I release a single or an EP next?",
    "How do I book my first paid show in DC?",
  ];

  return (
    <div className="min-h-screen bg-background flex" data-testid="ai-coach-page">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-72 bg-card/60 backdrop-blur-xl border-r border-white/10 transition-transform`}>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-500 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-black text-sm uppercase tracking-widest">AI Coach</h1>
              <p className="text-[10px] text-muted-foreground">Powered by Claude</p>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden">
              <X className="w-4 h-4" />
            </button>
          </div>
          <Button
            onClick={createSession}
            className="w-full mb-3 bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:from-fuchsia-600 hover:to-purple-600"
            data-testid="new-session-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New chat
          </Button>
          <div className="space-y-1 overflow-y-auto max-h-[calc(100vh-160px)]">
            {loadingSess ? (
              [...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)
            ) : sessions.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">No conversations yet</p>
            ) : sessions.map(s => (
              <div
                key={s.session_id}
                className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm ${
                  activeId === s.session_id ? 'bg-fuchsia-500/15 text-fuchsia-300' : 'hover:bg-muted/40'
                }`}
                onClick={() => { loadSession(s.session_id); setSidebarOpen(false); }}
                data-testid={`session-${s.session_id}`}
              >
                <MessageSquare className="w-4 h-4 shrink-0 opacity-60" />
                <span className="truncate flex-1">{s.title}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-400"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* Main chat */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden border-b border-white/10 p-3 flex items-center gap-2">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="font-black">AI Coach</h1>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 max-w-3xl mx-auto w-full">
          {!activeId ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4" data-testid="coach-welcome">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-500 flex items-center justify-center shadow-xl mb-4">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-black mb-2">Your AI Creator Coach</h1>
              <p className="text-sm text-muted-foreground mb-8 max-w-md">
                Claude Sonnet 4.5 trained on your DMV Soundstage stats. Ask anything about growth,
                releases, monetization, or your creator strategy.
              </p>
              <Button onClick={createSession} size="lg" className="bg-gradient-to-r from-fuchsia-500 to-purple-500 mb-8">
                <Plus className="w-4 h-4 mr-1" /> Start your first conversation
              </Button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                {suggestions.map(s => (
                  <button
                    key={s}
                    onClick={async () => { await createSession(); setInput(s); }}
                    className="p-3 rounded-xl bg-card/60 border border-white/10 hover:border-fuchsia-500/40 text-left text-xs transition-all"
                  >
                    <Sparkles className="w-3 h-3 text-fuchsia-400 inline mr-1" />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-10">
              Say hi. Ask anything.
            </div>
          ) : (
            messages.map(m => <Message key={m.message_id} msg={m} />)
          )}
          {sending && (
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-4">
              <Loader2 className="w-3 h-3 animate-spin" /> Coach is thinking…
            </div>
          )}
        </div>

        {activeId && (
          <div className="border-t border-white/10 p-4">
            <div className="max-w-3xl mx-auto flex gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask the coach anything…"
                className="flex-1 bg-card/60 backdrop-blur border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-fuchsia-500/60"
                disabled={sending}
                data-testid="coach-input"
              />
              <Button
                onClick={send}
                disabled={sending || !input.trim()}
                className="bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:from-fuchsia-600 hover:to-purple-600"
                data-testid="coach-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
